import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import toast from 'react-hot-toast';
import { Users, Calendar, ImageIcon, Link2, Wifi, Upload } from 'lucide-react';
import { Album, AlbumMember, FTPAccount } from '../../types';
import { albumsApi } from '../../api/albums';
import { ftpAccountsApi } from '../../api/ftpAccounts';
import { Modal } from '../UI/Modal';
import { Skeleton } from '../UI/Skeleton';
import { LiveBadge } from '../UI/LiveBadge';
import { MachhaCharacter } from '../../lib/machhaAsset';
import { getPortalUrl } from '../../lib/portal';
import styles from './AlbumDetailsModal.module.css';

interface AlbumDetailsModalProps {
  album: Album | null;
  isOpen: boolean;
  onClose: () => void;
  eventStatus?: 'active' | 'ended';
}

function formatDate(iso: string) {
  return new Date(iso).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function formatTime(iso: string) {
  return new Date(iso).toLocaleTimeString(undefined, {
    hour: 'numeric',
    minute: '2-digit',
  });
}

function timeAgo(iso: string) {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60_000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  return formatDate(iso);
}

export function AlbumDetailsModal({ album, isOpen, onClose, eventStatus }: AlbumDetailsModalProps) {
  const [members, setMembers] = useState<AlbumMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedFtpAccounts, setSelectedFtpAccounts] = useState<FTPAccount[]>([]);

  useEffect(() => {
    if (!album || !isOpen) return;
    setLoading(true);
    setSelectedFtpAccounts([]);

    const membersPromise = albumsApi
      .listMembers(album._id)
      .then((r) => setMembers(r.members))
      .catch(() => toast.error('Failed to load members'));

    const ftpPromise =
      album.isLive && album.sourceFilter === 'selected' && album.sourceFtpAccountIds?.length
        ? ftpAccountsApi
            .list(album.eventId)
            .then((accounts) =>
              setSelectedFtpAccounts(
                accounts.filter((a) => album.sourceFtpAccountIds!.includes(a._id)),
              ),
            )
            .catch(() => {})
        : Promise.resolve();

    Promise.all([membersPromise, ftpPromise]).finally(() => setLoading(false));
  }, [album?._id, isOpen]);

  if (!album) return null;

  const inviteUrl = `${getPortalUrl('viewer')}/join/${album.inviteToken}`;

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(inviteUrl);
      toast.success('Link copied!');
    } catch {
      toast.error('Failed to copy');
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Album Details" size="md">
      <div className={styles['container']}>
        {/* Album info header */}
        <div className={styles['header']}>
          <div className={styles['header-row']}>
            <h3 className={styles['album-name']}>
              {album.name}
            </h3>
            {album.isLive && eventStatus !== 'ended' && <LiveBadge active />}
          </div>
          {album.description && (
            <p className={styles['album-description']}>
              {album.description}
            </p>
          )}
        </div>

        {/* Stats row */}
        <div className={styles['stats-grid']}>
          <div className={styles['stat-card']}>
            <Users className={`${styles['icon-indigo']} ${styles['icon-md']}`} />
            <span className={styles['stat-value']}>
              {loading ? '–' : members.length}
            </span>
            <span className={styles['stat-label']}>
              Members
            </span>
          </div>
          <div className={styles['stat-card']}>
            <ImageIcon className={`${styles['icon-orange']} ${styles['icon-md']}`} />
            <span className={styles['stat-value']}>
              {album.processedPhotoCount ?? 0}
            </span>
            <span className={styles['stat-label']}>
              Photos
            </span>
          </div>
          <div className={styles['stat-card']}>
            <Calendar className={`${styles['icon-emerald']} ${styles['icon-md']}`} />
            <span className={styles['stat-date-value']}>
              {formatDate(album.createdAt)}
            </span>
            <span className={styles['stat-label']}>
              Created
            </span>
          </div>
        </div>

        {/* Live Configuration */}
        {album.isLive && (
          <div>
            <h4 className={styles['section-title']}>
              Live Configuration
            </h4>
            <div className={styles['live-config-card']}>
              {/* Source filter row */}
              <div className={styles['config-row']}>
                <div className={styles['config-label']}>
                  <Wifi className={`${styles['icon-indigo']} ${styles['icon-sm']}`} />
                  <span className={styles['config-label-text']}>
                    Photo sources
                  </span>
                </div>
                <span className={styles['config-value']}>
                  {album.sourceFilter === 'all'
                    ? 'All cameras'
                    : `${album.sourceFtpAccountIds?.length ?? 0} selected`}
                </span>
              </div>
              {/* Selected FTP account chips — render placeholder chips while loading
                  so the modal height is stable across the async transition */}
              {album.sourceFilter === 'selected' && (album.sourceFtpAccountIds?.length ?? 0) > 0 && (
                <div className={styles['ftp-chip-area']}>
                  {(loading ? album.sourceFtpAccountIds! : selectedFtpAccounts.map((a) => a._id)).map((id, idx) => {
                    const account = selectedFtpAccounts.find((a) => a._id === id);
                    return (
                      <span key={id ?? idx} className={styles['ftp-chip']}>
                        {account ? (
                          <>
                            <span className={styles['ftp-chip-name']}>{account.photographerName}</span>
                            <span className={styles['ftp-chip-username']}>{account.username}</span>
                          </>
                        ) : (
                          <>
                            <Skeleton className={styles['ftp-chip-name-skeleton']} />
                            <Skeleton className={styles['ftp-chip-username-skeleton']} />
                          </>
                        )}
                      </span>
                    );
                  })}
                </div>
              )}
              {/* Manual uploads row */}
              <div className={styles['config-row-last']}>
                <div className={styles['config-label']}>
                  <Upload className={`${styles['icon-emerald']} ${styles['icon-sm']}`} />
                  <span className={styles['config-label-text']}>
                    Manual uploads
                  </span>
                </div>
                <span
                  className={[
                    album.sourceIncludeManualUploads
                      ? styles['config-value-enabled']
                      : styles['config-value-disabled'],
                    styles['config-value-inline'],
                  ].join(' ')}
                >
                  {album.sourceIncludeManualUploads ? 'Allowed' : 'Disabled'}
                </span>
              </div>
            </div>
          </div>
        )}

        {/* Invite link */}
        <button
          onClick={handleCopyLink}
          className={styles['invite-link-button']}
        >
          <Link2 className={`${styles['invite-link-icon']} ${styles['icon-sm']}`} />
          <span className={styles['invite-link-text']}>
            {inviteUrl}
          </span>
          <span className={styles['invite-link-copy']}>
            Copy
          </span>
        </button>

        {/* Members list */}
        <div>
          <h4 className={styles['section-title']}>
            Members
          </h4>

          {loading ? (
            <div className={styles['skeleton-list']}>
              {/* Match the eventual member count so the modal height doesn't shift
                  when the API resolves. Cap at 5 to avoid long lists during load. */}
              {Array.from({ length: Math.min(Math.max(album.memberCount ?? 1, 1), 5) }).map((_, i) => (
                <div key={i} className={styles['skeleton-row']}>
                  <Skeleton className={styles['skeleton-avatar']} />
                  <div className={styles['skeleton-meta']}>
                    <Skeleton className={styles['skeleton-name']} />
                    <Skeleton className={styles['skeleton-email']} />
                  </div>
                </div>
              ))}
            </div>
          ) : members.length === 0 ? (
            <div className={styles['empty-state']}>
              <MachhaCharacter state="Empty" size={80} />
              <p className={styles['empty-text']}>
                No one has joined yet. Share the invite link above.
              </p>
            </div>
          ) : (
            <motion.div
              className={styles['members-list']}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.18, ease: 'easeOut' }}
            >
              {members.map((member) => (
                <div
                  key={member._id}
                  className={styles['member-card']}
                >
                  {/* Avatar */}
                  {member.userId.picture ? (
                    <img
                      src={member.userId.picture}
                      alt={member.userId.name}
                      referrerPolicy="no-referrer"
                      className={styles['member-avatar']}
                    />
                  ) : (
                    <div className={styles['member-avatar-placeholder']}>
                      {member.userId.name.charAt(0).toUpperCase()}
                    </div>
                  )}

                  {/* Name + email */}
                  <div className={styles['member-info']}>
                    <p className={styles['member-name']}>
                      {member.userId.name}
                    </p>
                    <p className={styles['member-email']}>
                      {member.userId.email}
                    </p>
                  </div>

                  {/* Join date */}
                  <div className={styles['member-date']}>
                    <p className={styles['member-time-ago']}>
                      {timeAgo(member.joinedAt)}
                    </p>
                    <p className={styles['member-date-detail']}>
                      {formatDate(member.joinedAt)} {formatTime(member.joinedAt)}
                    </p>
                  </div>
                </div>
              ))}
            </motion.div>
          )}
        </div>
      </div>
    </Modal>
  );
}
